import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { isDesktop } from 'react-device-detect';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import Cropper from 'cropperjs';
import { cloneDeep } from 'lodash';
import { shallow } from 'zustand/shallow';

import {
  CropperImage,
  LoadingSpinner,
  ResetTranslateModal,
  ScanTextImageModal,
  TranslateAnswerModal,
  UnavailableAllStrokeDataModal,
  UnavailableStrokeDataModal,
} from '../../components/Molecules';
import {
  ClubAccessRestrictionPopup,
  DiscardChangePopup,
  QuestionAnswerLanguageSelectPopup,
  SelectImageFileModePopup,
} from '../../components/Popups';
import {
  CopyScripturesTemplate,
  CreateMemoryGameContentEditTemplate,
} from '../../components/Templates';
import { StaminaType } from '../../constants/currencies';
import { SelectImageMode } from '../../constants/image';
import { getLanguageDetail } from '../../constants/languageDetails';
import { SVGIconType } from '../../constants/svgIcon';
import { AppToastErrorOption, AppToastInfoOption } from '../../constants/toast';
import { getWebBoosterVairant } from '../../constants/webBoosterType';
import { usePopup } from '../../context';
import useGameCurrency from '../../hooks/useGameCurrency';
import { TtsLocale, UiLocale } from '../../models/Base';
import {
  getQuickCreateGameModeProps,
  QuickCreateGameMode,
} from '../../models/DraftStore';
import { Stroke } from '../../models/FileStore';
import { useDraftStore } from '../../stores/DraftStore';
import { useSessionStore } from '../../stores/SessionStore';
import { useUserStore } from '../../stores/UserStore';
import { uiToTranslation } from '../../utils/i18n';
import {
  containsOnlyNotWord,
  splitIntoGraphme,
  splitIntoWords,
} from '../../utils/string';

const MAX_PAYLOAD_SIZE = 5 * 1024 * 1024; // 5 MB (adjust as needed)

enum PopupMode {
  AutoTranslate = 'autoTranslate',
  ResetTranslate = 'resetTranslate',
  ScanText = 'scanText',
  ShowCropImage = 'showCropImage',
  UnavailableStroke = 'unavailableStroke',
  UnavailableStrokeComplete = 'unavailableStrokeComplete',
  UnavailableAllStrokeComplete = 'unavailableAllStrokeComplete',
  CopyScripture = 'copyScripture',
}

interface State {
  type: 'Question' | 'Answer' | undefined;
  index: number | undefined;
  createType: QuickCreateGameMode;
  gameContent: string;
  soundText: string;
  placeHolder: string | undefined;
  maxTextLength: number;
  errorMessage: string;
  popupMode: PopupMode | undefined;
  selectedMode: SelectImageMode | undefined;
  cropImageSrc: string | undefined;
  missingStrokes: string[];
  strokeResolution: Record<string, Stroke>;
  selectedLanguage: TtsLocale | undefined;
  isUseTts: boolean;
  isAutoTranslateDisabled: boolean;
  isNextDisabled: boolean;
  isExceedLimit: boolean;
  nextCompletedStep: boolean;
  hasAutoTranslate: boolean;
  fromPage: string | undefined;
  isDisableBackground: boolean;
  isLoading: boolean;
}

const CreateMemoryGameContentEditScreen: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { showPopup, hidePopup } = usePopup();
  const location = useLocation();
  const [state, setState] = useState<State>({
    type: undefined,
    index: undefined,
    createType: QuickCreateGameMode.QuickMemory,
    gameContent: '',
    soundText: '',
    placeHolder: undefined,
    maxTextLength: 0,
    errorMessage: '',
    popupMode: undefined,
    selectedMode: undefined,
    cropImageSrc: undefined,
    missingStrokes: [],
    strokeResolution: {},
    selectedLanguage: undefined,
    isUseTts: true,
    isAutoTranslateDisabled: false,
    isNextDisabled: false,
    isExceedLimit: false,
    nextCompletedStep: false,
    hasAutoTranslate: false,
    fromPage: undefined,
    isDisableBackground: false,
    isLoading: false,
  });

  const activeUserId = useSessionStore((store) => store.activeUserId);
  const userAccount = useUserStore((store) =>
    store.getAccount(activeUserId ?? ''),
  );
  const userProfile = useUserStore((store) =>
    store.getProfile(activeUserId ?? ''),
  );

  const isWebPlanUnlocked = !!userAccount?.activeWebPlanTier;

  const imageElement = useRef<HTMLImageElement>(null);
  const cropper = useRef<Cropper>();

  const { mode } = useDraftStore((store) => {
    const _mode = store.getGameMode();
    return { mode: _mode };
  });

  const draft = useDraftStore((store) => {
    if (state.index !== undefined) {
      return store.getGameDraft(state.index);
    }
    return null;
  }, shallow);

  useEffect(() => {
    if (mode) {
      setState((s) => ({ ...s, createType: mode }));
    } else {
      navigate('/create/memory-game/select-type', { replace: true });
    }
  }, [mode, navigate]);

  const locale = useMemo(() => {
    if (state.type === 'Question') {
      if (draft?.game.question.locale) {
        return draft?.game.question.locale;
      }
    } else if (state.type === 'Answer') {
      if (draft?.game.answer.locale) {
        return draft?.game.answer.locale;
      }
    }
    return useDraftStore.getState().getLocale();
  }, [draft?.game.answer.locale, draft?.game.question.locale, state.type]);

  const recentLocale = useMemo(() => {
    if (state.type === 'Question') {
      return useDraftStore.getState().getRecentQuestionLocale();
    }
    return useDraftStore.getState().getRecentAnswerLocale();
  }, [state.type]);

  useEffect(() => {
    i18n
      .loadLanguages([
        uiToTranslation((state.selectedLanguage ?? locale) as UiLocale),
      ])
      .catch(console.error)
      .finally(() => {
        setState((s) => ({ ...s, rerednerState: true }));
      });
  }, [i18n, locale, state.selectedLanguage]);

  const isDirty = useMemo(() => {
    if (state.type === 'Question') {
      return (
        state.gameContent !== draft?.game.question.text?.text ||
        state.selectedLanguage !== draft?.game.question.locale
      );
    } else if (state.type === 'Answer') {
      return (
        state.gameContent !== draft?.game.answer.text?.text ||
        state.selectedLanguage !== draft?.game.answer.locale
      );
    }
    return false;
  }, [
    draft?.game.answer.locale,
    draft?.game.answer.text?.text,
    draft?.game.question.locale,
    draft?.game.question.text?.text,
    state.gameContent,
    state.selectedLanguage,
    state.type,
  ]);

  useEffect(() => {
    if (locale) {
      setState((s) => ({ ...s, selectedLanguage: locale }));
    }
  }, [locale]);

  useEffect(() => {
    if (draft === undefined) {
      navigate(-1);
    } else {
      if (state.type === 'Question') {
        setState((s) => ({
          ...s,
          gameContent: draft?.game.question.text?.text ?? '',
          soundText: draft?.game.question.text.soundText ?? '',
          maxTextLength: draft?.game.question.text?.maxLength ?? 0,
          placeHolder: draft?.game.question.text?.placeholder,
          isUseTts: draft?.game.question.hasTts ?? true,
          hasAutoTranslate: draft?.game.question.text.hasAutoTranslate ?? false,
          isAutoTranslateDisabled: draft?.game.answer.text.text ? false : true,
        }));
      } else {
        setState((s) => ({
          ...s,
          gameContent: draft?.game.answer.text?.text ?? '',
          // soundText: draft?.game.answer.text.soundText ?? '',
          missingStrokes: draft?.game.answer.text.missingStrokes ?? [],
          strokeResolution: draft?.game.answer.text.strokeResolution ?? {},
          maxTextLength: draft?.game.answer.text?.maxLength ?? 0,
          placeHolder: draft?.game.answer.text?.placeholder,
          isUseTts: draft?.game.answer.hasTts ?? true,
          isAutoTranslateDisabled: true,
          hasAutoTranslate: draft?.game.question.text.hasAutoTranslate ?? false,
        }));
      }
    }
  }, [draft, navigate, state.type]);

  useEffect(() => {
    if (location.state) {
      const type = (location.state as { type: 'Question' | 'Answer' }).type;
      const index = (location.state as { index: number }).index;
      const fromPage = (location.state as { fromPage: string | undefined })
        .fromPage;

      if (index === undefined) {
        navigate(-1);
      }
      setState((s) => ({
        ...s,
        type: type,
        index: index,
        fromPage: fromPage,
      }));
    } else {
      navigate('/create/memory-game/select-type', { replace: true });
    }
  }, [location.state, navigate]);

  const onSwitchTts = useCallback(() => {
    setState((s) => ({ ...s, isUseTts: !s.isUseTts }));
  }, []);

  const onClickComplete = useCallback(() => {
    const gameDraft = cloneDeep(draft);
    if (gameDraft) {
      if (state.type === 'Question') {
        gameDraft.game.question.text.text = state.gameContent;
        gameDraft.game.question.hasTts = state.isUseTts;
        gameDraft.game.question.text.hasAutoTranslate = state.hasAutoTranslate;
        gameDraft.game.question.text.soundText =
          state.hasAutoTranslate ||
          state.createType === QuickCreateGameMode.QuickMemory
            ? state.gameContent
            : gameDraft.game.answer.text.text;
        gameDraft.game.question.locale =
          state.selectedLanguage ?? gameDraft.game.question.locale;
      } else {
        gameDraft.game.answer.text.text = state.gameContent;
        gameDraft.game.answer.text.missingStrokes = state.missingStrokes;
        gameDraft.game.answer.text.strokeResolution = state.strokeResolution;
        // gameDraft.game.answer.hasTts = state.isUseTts;
        gameDraft.game.answer.locale =
          state.selectedLanguage ?? gameDraft.game.answer.locale;
        gameDraft.locale = state.selectedLanguage ?? gameDraft.locale;
        gameDraft.game.question.text.soundText =
          state.hasAutoTranslate ||
          state.createType === QuickCreateGameMode.QuickMemory
            ? gameDraft.game.question.text.soundText
            : state.gameContent;
      }
      useDraftStore.getState().editGameDraft(state.index ?? 0, gameDraft);
    }
    if (state.fromPage === 'selectType') {
      navigate('/create/memory-game/generated-games', { replace: true });
    } else {
      navigate(-1);
    }
  }, [
    draft,
    navigate,
    state.createType,
    state.fromPage,
    state.gameContent,
    state.hasAutoTranslate,
    state.index,
    state.isUseTts,
    state.missingStrokes,
    state.selectedLanguage,
    state.strokeResolution,
    state.type,
  ]);

  useEffect(() => {
    if (state.nextCompletedStep) {
      setState((s) => ({
        ...s,
        nextCompletedStep: false,
      }));
      onClickComplete();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.nextCompletedStep]); //onClickComplete will make it loop

  const startStrokeResolution = useCallback(async () => {
    if (mode === QuickCreateGameMode.HandWriting && state.type === 'Answer') {
      setState((s) => ({ ...s, isLoading: true }));
      const missingStroke = new Set<string>();
      let isAllMissingStroke = true;
      let strokeDatas: Record<string, Stroke> = {};
      if (state.gameContent && state.selectedLanguage) {
        strokeDatas = await useDraftStore
          .getState()
          .startStrokeResolution(state.gameContent, state.selectedLanguage);

        splitIntoGraphme(state.gameContent, locale).forEach((content) => {
          if (content.isWordLike && !strokeDatas[content.text]) {
            missingStroke.add(content.text);
          } else if (content.isWordLike && strokeDatas[content.text]) {
            isAllMissingStroke = false;
          }
        });
      }

      setState((s) => ({
        ...s,
        missingStrokes: [...missingStroke],
        strokeResolution: strokeDatas,
        isLoading: false,
      }));

      if (missingStroke.size > 0) {
        return { isAllMissingStroke, missingStrokes: [...missingStroke] };
      }
    }
    return undefined;
  }, [locale, mode, state.gameContent, state.selectedLanguage, state.type]);

  const onClickNext = useCallback(async () => {
    const strokeResolution = await startStrokeResolution();

    if (containsOnlyNotWord(state.gameContent)) {
      setState((s) => ({
        ...s,
        errorMessage: 'Cannot be created using emojis/punctuation only.',
      }));
    } else if (strokeResolution) {
      if (strokeResolution.isAllMissingStroke) {
        setState((s) => ({
          ...s,
          popupMode: PopupMode.UnavailableAllStrokeComplete,
          missingStrokes: [...strokeResolution.missingStrokes],
        }));
      } else {
        setState((s) => ({
          ...s,
          popupMode: PopupMode.UnavailableStrokeComplete,
          missingStrokes: [...strokeResolution.missingStrokes],
        }));
      }
    } else {
      setState((s) => ({
        ...s,
        nextCompletedStep: true,
      }));
    }
  }, [startStrokeResolution, state.gameContent]);

  const onDiscard = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const onClickBack = useCallback(() => {
    if (isDirty) {
      showPopup(
        <DiscardChangePopup
          onDiscardChange={() => {
            hidePopup();
            onDiscard();
          }}
          onPrimary={hidePopup}
        />,
      );
    } else {
      onDiscard();
    }
  }, [hidePopup, isDirty, onDiscard, showPopup]);

  const onGameContentChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      setState((s) => ({
        ...s,
        gameContent: event.target.value,
        errorMessage: '',
      }));
    },
    [],
  );

  const onSetRecentLocale = useCallback(
    (selectedLocale: TtsLocale) => {
      if (state.type === 'Question') {
        useDraftStore.getState().setRecentQuestionLocale(selectedLocale);
      } else {
        useDraftStore.getState().setRecentAnswerLocale(selectedLocale);
      }
    },
    [state.type],
  );

  const handleLanguagePopup = useCallback(
    (selectedLocale: TtsLocale) => {
      const languageList = getQuickCreateGameModeProps(
        state.createType,
        userAccount?.settings.webBoosterType,
      ).languageList;
      setState((s) => ({ ...s, isDisableBackground: true }));

      showPopup(
        <QuestionAnswerLanguageSelectPopup
          title={`Language of ${state.type} (${
            getWebBoosterVairant(userAccount?.settings.webBoosterType)
              .shortCreateName
          })`}
          titleIcon={
            state.type === 'Question'
              ? SVGIconType.LanguageQ
              : SVGIconType.LanguageA
          }
          languageList={state.type === 'Answer' ? languageList : undefined}
          selectedLanguage={selectedLocale}
          recentLanguage={recentLocale}
          onClickOK={() => {
            hidePopup();
            setState((s) => ({ ...s, isDisableBackground: false }));
          }}
          onCancel={() => {
            hidePopup();
            setState((s) => ({ ...s, isDisableBackground: false }));
          }}
          onSelectLanguage={(language: TtsLocale) => {
            setState((s) => ({ ...s, selectedLanguage: language }));
            // useDraftStore.getState().setLocale(language);
            onSetRecentLocale(language);
            handleLanguagePopup(language); // Re-show popup with updated locale
          }}
        />,
        {
          backdropCallback: () => {
            hidePopup();
            setState((s) => ({ ...s, isDisableBackground: false }));
          },
        },
      );
    },
    [
      state.createType,
      state.type,
      userAccount?.settings.webBoosterType,
      showPopup,
      recentLocale,
      hidePopup,
      onSetRecentLocale,
    ],
  );

  const onClickLanguage = useCallback(() => {
    if (state.selectedLanguage) {
      handleLanguagePopup(state.selectedLanguage);
    }
  }, [handleLanguagePopup, state.selectedLanguage]);

  useEffect(() => {
    const isExceedLimit =
      splitIntoWords(state.gameContent, state.selectedLanguage ?? locale, {
        withSpace: false,
      }).length > state.maxTextLength;
    if (state.gameContent && !isExceedLimit) {
      setState((s) => ({ ...s, isNextDisabled: false, isExceedLimit: false }));
    } else {
      if (isExceedLimit) {
        setState((s) => ({ ...s, isExceedLimit: true }));
      }
      setState((s) => ({ ...s, isNextDisabled: true }));
    }
  }, [locale, state.selectedLanguage, state.gameContent, state.maxTextLength]);

  const onClickAudioBar = useCallback(() => {
    toast(t(`Listen to the voice (Text-to-speech) in the practice.`), {
      ...AppToastInfoOption,
      id: 'tts',
    });
  }, [t]);

  const allowance = useUserStore((store) => store.getAllowance());

  const { fetchCurrency, checkBalance } = useGameCurrency(
    userAccount,
    userProfile,
    allowance,
  );

  const onAutoTranslate = useCallback(async () => {
    if (draft) {
      setState((s) => ({ ...s, isLoading: true }));
      const translationText = await useDraftStore
        .getState()
        .autoTranslateAnswer(
          draft.game.answer.text.text,
          draft.game.answer.locale,
          state.selectedLanguage ?? locale,
        );
      setState((s) => ({ ...s, isLoading: false }));
      if (translationText) {
        setState((s) => ({
          ...s,
          gameContent: translationText,
          hasAutoTranslate: true,
        }));
      }
    }
    setState((s) => ({ ...s, popupMode: undefined }));
  }, [draft, locale, state.selectedLanguage]);

  const onResetTranslate = useCallback(() => {
    const gameModeProps = getQuickCreateGameModeProps(
      state.createType,
      userAccount?.settings.webBoosterType,
    );
    const defaultQuestionText = t(gameModeProps.defaultQuestionText, {
      lng: state.selectedLanguage,
    });
    setState((s) => ({
      ...s,
      gameContent: defaultQuestionText,
      hasAutoTranslate: false,
      popupMode: undefined,
    }));
  }, [
    state.createType,
    state.selectedLanguage,
    t,
    userAccount?.settings.webBoosterType,
  ]);

  const onClickAutoTranslate = useCallback(() => {
    if (state.isAutoTranslateDisabled) {
      toast(t('No answers text yet'), AppToastInfoOption);
    } else {
      setState((s) => ({ ...s, popupMode: PopupMode.AutoTranslate }));
    }
  }, [state.isAutoTranslateDisabled, t]);

  const onClickResetTranslate = useCallback(() => {
    setState((s) => ({ ...s, popupMode: PopupMode.ResetTranslate }));
  }, []);

  const onCopyScriptures = useCallback(() => {
    setState((s) => ({ ...s, popupMode: PopupMode.CopyScripture }));
  }, []);

  useEffect(() => {
    const handlePopState = (e: PopStateEvent) => {
      e.preventDefault();
      onClickBack();
    };

    const handleBeforeUnload = (e: BeforeUnloadEvent): string | undefined => {
      e.preventDefault();
      return '';
    };
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    // const currentState = window.history.state;
    // window.history.pushState(currentState, '', window.location.pathname);
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hidePopup, navigate, onClickBack, showPopup]);

  const onClickMissingStrokeData = useCallback(() => {
    setState((s) => ({
      ...s,
      popupMode: PopupMode.UnavailableStroke,
    }));
  }, []);

  const onPressCancel = useCallback(() => {
    setState((s) => ({ ...s, popupMode: undefined }));
  }, []);

  const soundText = useMemo(() => {
    if (
      state.hasAutoTranslate ||
      state.createType === QuickCreateGameMode.QuickMemory
    ) {
      return state.gameContent;
    } else {
      return draft?.game.answer.text.text ?? '';
    }
  }, [draft, state.createType, state.gameContent, state.hasAutoTranslate]);

  const onClickPaste = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText();
      setState((s) => ({ ...s, gameContent: s.gameContent + text }));
    } catch {
      toast(
        t('Access to the text you copied to the clipboard is blocked.'),
        AppToastErrorOption,
      );
    }
  }, [t]);

  const onClickScanLanguage = useCallback(() => {
    if (state.selectedLanguage) {
      handleLanguagePopup(state.selectedLanguage);
    }
  }, [handleLanguagePopup, state.selectedLanguage]);

  const getBase64 = useCallback(
    async (file: File): Promise<string | undefined> => {
      const checkPayloadSize = (base64String: string): boolean => {
        const byteLength = (base64String.length * 6) / 8;
        return byteLength >= MAX_PAYLOAD_SIZE;
      };

      return new Promise<string | undefined>((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          let base64String = reader.result as string;

          const resizeImage = (
            imageData: string,
          ): Promise<string | undefined> => {
            return new Promise<string | undefined>((resolve2) => {
              const img = new Image();
              img.onload = () => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');

                // Resize the image to 0.9 times its original width and height
                const newWidth = img.width * 0.9;
                const newHeight = img.height * 0.9;
                canvas.width = newWidth;
                canvas.height = newHeight;
                ctx?.drawImage(img, 0, 0, newWidth, newHeight);

                // Convert the resized image on the canvas back to a base64 string
                resolve2(canvas.toDataURL('image/jpeg')); // Extract the base64 string part
              };
              img.src = imageData;
            });
          };

          const processImage = async () => {
            while (checkPayloadSize(base64String)) {
              base64String = (await resizeImage(base64String)) ?? '';
            }
          };

          processImage()
            .then(() => {
              const rawBase64String = base64String.split(',')[1];
              resolve(rawBase64String);
            })
            .catch(console.error);
        };
        reader.onerror = (error) => reject(error);
      });
    },
    [],
  );

  const handleFileChange = (file: File | undefined | null) => {
    setState((s) => ({ ...s, popupMode: PopupMode.ShowCropImage }));
    const reader = new FileReader();

    if (file) {
      reader.readAsDataURL(file);
    }

    reader.onload = () => {
      setState((s) => ({ ...s, cropImageSrc: reader.result as string }));
    };
  };

  useEffect(() => {
    if (imageElement.current && state.cropImageSrc) {
      imageElement.current.src = state.cropImageSrc;
      cropper.current = new Cropper(imageElement.current, {
        viewMode: 1,
        background: false,
      });
    }
  }, [state.cropImageSrc]);

  const onSelectedFile = useCallback(
    async (file: File | undefined | null) => {
      if (file) {
        const base64Image = await getBase64(file);
        if (base64Image) {
          setState((s) => ({ ...s, isLoading: true }));
          const detectedText = await useDraftStore
            .getState()
            .scanTextFromImage(base64Image, state.selectedLanguage);
          setState((s) => ({
            ...s,
            isLoading: false,
          }));

          fetchCurrency().catch(console.error);

          if (detectedText) {
            setState((s) => ({ ...s, gameContent: detectedText }));
          } else {
            // error fail to get the detectedText or return the null (no text detected)
            toast(t('There is something wrong.'), AppToastErrorOption);
          }
        } else {
          // error fail to change to base64
          toast(t('There is something wrong.'), AppToastErrorOption);
          setState((s) => ({ ...s, isLoading: false }));
        }
      }
    },
    [fetchCurrency, getBase64, state.selectedLanguage, t],
  );

  const onCancelCrop = useCallback(() => {
    cropper.current = undefined;
    setState((s) => ({ ...s, popupMode: undefined, cropImageSrc: undefined }));
  }, []);

  const onClickDoneCrop = useCallback(async () => {
    if (cropper.current) {
      const croppedCanvas = cropper.current.getCroppedCanvas();

      const createCroppedFile = await new Promise<File>((resolve, reject) => {
        croppedCanvas.toBlob((blob) => {
          if (blob) {
            const file = new File([blob], 'cropped-image.jpg');
            resolve(file);
          } else {
            reject(new Error('Blob creation failed'));
          }
        });
      });

      onCancelCrop();
      await onSelectedFile(createCroppedFile);
    }
  }, [onCancelCrop, onSelectedFile]);

  const onSelectMode = useCallback(
    (selectedMode: SelectImageMode) => {
      hidePopup();
      setState((s) => ({
        ...s,
        popupMode: PopupMode.ScanText,
        selectedMode: selectedMode,
      }));
    },
    [hidePopup],
  );

  const onClubAccessRestricted = useCallback(() => {
    showPopup(
      <ClubAccessRestrictionPopup
        onDetails={() => {
          hidePopup();
          navigate('/subscription');
        }}
        onCancel={hidePopup}
      />,
    );
  }, [showPopup, hidePopup, navigate]);

  const onClickScanText = useCallback(() => {
    if (isWebPlanUnlocked) {
      if (isDesktop) {
        setState((s) => ({
          ...s,
          popupMode: PopupMode.ScanText,
          selectedMode: SelectImageMode.UploadImage,
        }));
      } else {
        setState((s) => ({
          ...s,
          selectedMode: undefined,
        }));
        showPopup(
          <SelectImageFileModePopup
            onPrimary={hidePopup}
            onSelectMode={onSelectMode}
          />,
        );
      }
    } else {
      onClubAccessRestricted();
    }
  }, [
    hidePopup,
    isWebPlanUnlocked,
    onClubAccessRestricted,
    onSelectMode,
    showPopup,
  ]);

  return (
    <>
      {state.popupMode === PopupMode.CopyScripture ? (
        <CopyScripturesTemplate
          type={state.createType}
          webBoosterType={userAccount?.settings.webBoosterType}
          onClickBack={onPressCancel}
        />
      ) : (
        <CreateMemoryGameContentEditTemplate
          type={state.createType}
          webBoosterType={userAccount?.settings.webBoosterType}
          mode={state.type}
          selectedLanguage={state.selectedLanguage ?? locale}
          language={
            getLanguageDetail(state.selectedLanguage)?.displayName ?? ''
          }
          isUseTts={state.isUseTts}
          soundText={soundText}
          gameContent={{
            value: state.gameContent,
            placeHolder: state.placeHolder,
            missingStrokes: state.missingStrokes,
            onChange: onGameContentChange,
          }}
          textLength={
            splitIntoWords(
              state.gameContent,
              state.selectedLanguage ?? locale,
              {
                withSpace: false,
              },
            ).length
          }
          maxTextLength={state.maxTextLength}
          hasAutoTranslate={state.hasAutoTranslate}
          isAutoTranslateDisabled={state.isAutoTranslateDisabled}
          isNextDisabled={state.isNextDisabled}
          isExceedLimit={state.isExceedLimit}
          errorMessage={state.errorMessage}
          onClickLanguage={onClickLanguage}
          onSwitchTts={onSwitchTts}
          onClickAutoTranslate={onClickAutoTranslate}
          onClickResetTranslate={onClickResetTranslate}
          onCopyScriptures={onCopyScriptures}
          onClickAudioBar={onClickAudioBar}
          onClickMissingStrokeData={onClickMissingStrokeData}
          onClickPaste={() => {
            onClickPaste().catch(console.error);
          }}
          onClickScan={onClickScanText}
          onClickOK={() => {
            onClickNext().catch(console.error);
          }}
          onClickBack={onClickBack}
        />
      )}
      {state.popupMode === PopupMode.AutoTranslate && (
        <TranslateAnswerModal
          type={state.createType}
          language={
            getLanguageDetail(state.selectedLanguage)?.displayName ?? ''
          }
          currencyType={StaminaType.CreationStamina}
          fee={1}
          isDisableBackground={state.isDisableBackground}
          onPrimary={() => {
            checkBalance(
              StaminaType.CreationStamina,
              1,
              () => {
                onAutoTranslate().then(fetchCurrency).catch(console.error);
              },
              { onShowPopup: onPressCancel },
            );
          }}
          onPressCancel={onPressCancel}
          onClickLanguage={onClickLanguage}
        />
      )}
      {state.popupMode === PopupMode.ResetTranslate && (
        <ResetTranslateModal
          type={state.createType}
          language={
            getLanguageDetail(state.selectedLanguage)?.displayName ?? ''
          }
          isDisableBackground={state.isDisableBackground}
          onPrimary={onResetTranslate}
          onPressCancel={onPressCancel}
          onClickLanguage={onClickLanguage}
        />
      )}
      {state.popupMode === PopupMode.ScanText && (
        <ScanTextImageModal
          selectedMode={state.selectedMode}
          hasOriginalContent={!!state.gameContent}
          language={
            getLanguageDetail(state.selectedLanguage)?.displayName ?? ''
          }
          currencyType={StaminaType.CreationStamina}
          fee={1}
          isDisableBackground={state.isDisableBackground}
          checkBalance={checkBalance}
          onPrimary={handleFileChange}
          onClickLanguage={onClickScanLanguage}
          onPressCancel={onPressCancel}
        />
      )}
      {state.popupMode === PopupMode.ShowCropImage && (
        <CropperImage
          imageRef={imageElement}
          cropperRef={cropper}
          onClickCancel={onCancelCrop}
          onClickDone={() => {
            onClickDoneCrop().catch(console.error);
          }}
        />
      )}
      {/* show popup cannot use the latest state */}
      {state.popupMode === PopupMode.UnavailableStroke && (
        <UnavailableStrokeDataModal
          missingStrokes={state.missingStrokes}
          onPrimary={onPressCancel}
          onCancel={onPressCancel}
        />
      )}
      {state.popupMode === PopupMode.UnavailableStrokeComplete && (
        <UnavailableStrokeDataModal
          missingStrokes={state.missingStrokes}
          onPrimary={onClickComplete}
          onCancel={onPressCancel}
        />
      )}
      {state.popupMode === PopupMode.UnavailableAllStrokeComplete && (
        <UnavailableAllStrokeDataModal onCancel={onPressCancel} />
      )}
      {state.isLoading && <LoadingSpinner loading={state.isLoading} />}
    </>
  );
};
export default CreateMemoryGameContentEditScreen;
